<script lang="ts">
import type { Header, RowData } from '@tanstack/vue-table';
import type { ComponentPublicInstance, CSSProperties } from 'vue';

export interface DataTableHeaderCellProps<T extends RowData> {
	header: Header<T, unknown>;
	orderable: boolean;
	orderableIndex: number;
	/** DnD Kit sortable group matching the column's TanStack pin partition. */
	sortableGroup: 'left' | 'center' | 'right';
	isResizingColumn: boolean;
	lastLeftPinned?: boolean;
	autoCapped?: boolean;
	relaxed?: boolean;
	/** True while this column is being explicitly auto-fit (uncapped content measurement). */
	autofitMeasuring?: boolean;
	/** Whether rendered header content should be visible while preserving intrinsic measurement. */
	contentVisible?: boolean;
	headerElement: HTMLElement | ComponentPublicInstance | null | undefined;
	columnStyles: CSSProperties;
}
</script>

<script setup lang="ts" generic="T extends RowData">
import { RestrictToHorizontalAxis } from '@dnd-kit/abstract/modifiers';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { useDragDropMonitor } from '@dnd-kit/vue';
import { useSortable } from '@dnd-kit/vue/sortable';
import { FlexRender } from '@tanstack/vue-table';
import { unrefElement } from '@vueuse/core';
import { computed, ref, useTemplateRef } from 'vue';

const {
	orderable,
	header,
	orderableIndex,
	sortableGroup,
	isResizingColumn,
	headerElement,
	columnStyles,
} = defineProps<DataTableHeaderCellProps<T>>();

const thRef = useTemplateRef('th');
const fixedElementWidth = ref<number | null>(null);

if (orderable) {
	useSortable({
		id: () => header.id,
		index: () => orderableIndex,
		group: () => sortableGroup,
		disabled: () => isResizingColumn,
		element: thRef,
		handle: thRef,
		// TODO: move modifiers to DragDropProvider once fixed upstream https://github.com/clauderic/dnd-kit/pull/1899
		modifiers: [
			RestrictToHorizontalAxis,
			RestrictToElement.configure({
				element: () => unrefElement(headerElement) ?? null,
			}),
		],
	});
}

const mergedStyles = computed(() => ({
	...columnStyles,
	...(fixedElementWidth.value
		? {
				width: `${fixedElementWidth.value}px`,
			}
		: {}),
}));

useDragDropMonitor({
	onBeforeDragStart() {
		fixedElementWidth.value = thRef.value?.getBoundingClientRect().width ?? null;
	},
	onDragEnd() {
		fixedElementWidth.value = null;
	},
});
</script>

<template>
	<th
		ref="th"
		:style="mergedStyles"
		:data-column-id="header.id"
		:data-pinned="header.column.getIsPinned()"
		:data-last-left-pinned="lastLeftPinned || undefined"
		:data-orderable="orderable"
		:data-auto-capped="autoCapped || undefined"
		:data-relaxed="relaxed || undefined"
		:data-autofit-measuring="autofitMeasuring || undefined"
	>
		<!-- Keep hidden header labels in layout so auto-sized tracks can still measure intrinsic content. -->
		<span
			class="contents"
			:class="{ invisible: contentVisible === false }"
			data-header-content
		>
			<slot>
				<FlexRender :render="header.column.columnDef.header" :props="header.getContext()" class="font-semibold" />
			</slot>
		</span>
	</th>
</template>
